import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_CMS_SERVICE } from '@/api/constant';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import Modal from '@/components/common/models/Modal';
import StatusDropdown from '@/components/common/StatusComponent';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { getPermissionName, PERMISSIONS } from '@/context/permission';
import { usePermissions } from '@/hooks/usePermissions';
import { IAppLayout } from '@/interfaces/appLayout.interface';
import { ActionConfig } from '@/interfaces/common.interface';
import { Info } from 'lucide-react';
import { useMemo, useState } from 'react';

interface StateProps {
  isLoading: boolean;
  selectedLayout: IAppLayout | null;
  showEditModel: boolean;
  error: string | null;
  updatedTitle: string | null;
  statusToUpdate: string | null;
  showUpdateModel: boolean;
}

interface Props {
  data: IAppLayout[];
  isLoading: boolean;
  onSuccess: () => void;
}

const AppLayoutTable: React.FC<Props> = ({ data, onSuccess, isLoading }) => {
  const { canEdit } = usePermissions();

  const [states, setStates] = useState<StateProps>({
    isLoading: false,
    selectedLayout: null,
    showEditModel: false,
    error: null,
    updatedTitle: null,
    showUpdateModel: false,
    statusToUpdate: null,
  });

  const handleChangeState = (newState: Partial<StateProps>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const callUpdateApi = async (data: IAppLayout[]) => {
    try {
      const PATH = `${API_URL_CMS_SERVICE}${API_ENDPOINT.homeLayout}`;
      await callApi(PATH, {
        method: 'PUT',
        data: { sections: data },
      });
      onSuccess();
      handleChangeState({ selectedLayout: null });
    } catch (error) {
      ThrowError(error);
    }
  };

  const layoutActionConfig: Record<string, ActionConfig<IAppLayout>> = {
    eidt: {
      text: 'Edit',
      onClick: (item) =>
        handleChangeState({
          selectedLayout: item,
          updatedTitle: item.title,
          showEditModel: true,
        }),
      permission: () => canEdit(getPermissionName(PERMISSIONS.EDIT_SETTINGS)),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(layoutActionConfig)
        .filter((action) => action.permission())
        .map(({ permission, ...rest }) => rest),
    [canEdit],
  );

  const columns: TableColumn<IAppLayout>[] = [
    {
      key: 'title',
      header: 'Title',
      className: 'py-6!',
    },
    {
      key: 'key',
      header: 'Key',
    },
    {
      key: 'rank',
      header: 'Rank',
    },
    {
      key: 'viewType',
      header: 'View Type',
    },
    {
      key: 'isEnabled',
      header: 'Status',
      render: (i) => {
        return (
          <StatusDropdown
            onChange={(val) => {
              handleChangeState({
                selectedLayout: i,
                showUpdateModel: true,
                statusToUpdate: val,
              });
            }}
            options={[
              {
                label: 'enable',
                value: 'enable',
                textClass: 'text-green-500',
                bgClass: 'bg-green-500',
              },
              {
                label: 'disable',
                value: 'disable',
                textClass: 'text-red-500',
                bgClass: 'bg-red-500',
              },
            ]}
            canEditVal={getPermissionName(PERMISSIONS.EDIT_SETTINGS)}
            value={i.isEnabled ? 'enable' : 'disable'}
          />
        );
      },
    },
  ];

  const updateData = (newTitle: string | null, newStatus: string | null) => {
    if (!states.selectedLayout) return;
    const payload: IAppLayout = {
      ...states.selectedLayout,
      title: newTitle !== null ? newTitle : states.selectedLayout.title,
      isEnabled:
        newStatus !== null
          ? newStatus === 'enable'
            ? true
            : false
          : states.selectedLayout.isEnabled,
    };

    callUpdateApi([payload]);
    handleChangeState({ showEditModel: false, selectedLayout: null });
  };

  const handleChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    handleChangeState({ updatedTitle: value });

    if (!value || value.length < 3) {
      handleChangeState({ error: 'Please enter variant name atleast 3 char.' });
    } else {
      handleChangeState({ error: '' });
    }
  };

  return (
    <Card>
      <CommonTable
        columns={columns}
        loading={isLoading}
        data={data}
        actions={actions}
        getItemId={(row) => row.id}
      />

      {states.showEditModel && (
        <Modal
          show={states.showEditModel}
          onClose={() =>
            handleChangeState({ showEditModel: false, selectedLayout: null })
          }
          title="Update Layout"
        >
          <div className="p-4 h-full flex flex-col justify-between space-y-4">
            <div>
              <FormField
                value={states.updatedTitle as string}
                onChange={(e) =>
                  handleChangeTitle(e as React.ChangeEvent<HTMLInputElement>)
                }
                placeholder="Enter variant name"
                required
              />
              {states.error && (
                <span
                  className={`text-red-500 text-sm flex items-start gap-1 mt-1 `}
                >
                  <Info size={16} /> {states.error}
                </span>
              )}
            </div>
            <div className="flex justify-end space-x-4">
              <button
                className="btn-secondary"
                onClick={() =>
                  handleChangeState({
                    showEditModel: false,
                    selectedLayout: null,
                  })
                }
                disabled={states.isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => updateData(states.updatedTitle, null)}
                className="btn-primary"
                disabled={states.isLoading}
              >
                {states.isLoading ? 'Submitting...' : 'Update'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {states.showUpdateModel && (
        <ConfirmationModel
          modelType="update"
          show={states.showUpdateModel}
          onClose={() => {
            handleChangeState({
              showUpdateModel: false,
              selectedLayout: null,
            });
          }}
          subTitle={`This action will update the status to ${states.statusToUpdate}`}
          onConfirm={() => updateData(null, states.statusToUpdate as string)}
        />
      )}
    </Card>
  );
};

export default AppLayoutTable;
